import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { StripeCheckoutModal } from '../components/StripeCheckout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ShoppingCart, Trash2, Plus, Minus, ArrowLeft, 
  Truck, Package, Wrench, MapPin, CreditCard, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const shippingOptions = [
  { id: 'standard', name: 'Standard Shipping', price: 7.99, days: '5-7 business days' },
  { id: 'express', name: 'Express Shipping', price: 14.99, days: '2-3 business days' },
  { id: 'overnight', name: 'Overnight Shipping', price: 24.99, days: '1 business day' },
  { id: 'pickup', name: 'In-Person Pickup', price: 0, days: 'Arrange with seller' }
];

export default function Cart() {
  const { cartItems, removeFromCart, updateQuantity, clearCart, getCartTotal, getServiceTotal, getProductTotal } = useCart();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [shipping, setShipping] = useState('standard');
  const [checkoutType, setCheckoutType] = useState('all'); // all, services, products
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);

  const services = cartItems.filter(item => item.type === 'service');
  const products = cartItems.filter(item => item.type === 'product');

  const selectedShipping = shippingOptions.find(s => s.id === shipping);
  
  const calculateTotal = () => {
    let total = 0;
    
    if (checkoutType === 'all' || checkoutType === 'services') {
      total += getServiceTotal();
    }
    
    if (checkoutType === 'all' || checkoutType === 'products') {
      total += getProductTotal();
      if (products.length > 0 && shipping !== 'pickup') {
        total += selectedShipping?.price || 0;
      }
    }
    
    return total;
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Please login to checkout');
      navigate('/login');
      return;
    }

    setLoading(true);
    
    try {
      // Create cart order on backend
      const response = await axios.post(
        `${API}/cart/checkout`,
        { 
          items: cartItems,
          checkout_type: checkoutType,
          shipping_method: shipping,
          shipping_cost: selectedShipping?.price || 0
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      // Store order ID and open payment modal
      setCurrentOrderId(response.data.order_id);
      setShowPaymentModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    toast.success('Order placed successfully!');
    
    // Clear checked out items
    if (checkoutType === 'all') {
      clearCart();
    } else if (checkoutType === 'services') {
      services.forEach(s => removeFromCart(s.id, 'service'));
    } else {
      products.forEach(p => removeFromCart(p.id, 'product'));
    }
    
    navigate('/customer');
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-12">
          <Card>
            <CardContent className="py-16 text-center">
              <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Cart is Empty</h2>
              <p className="text-gray-600 mb-6">Add services or products to get started</p>
              <div className="flex gap-4 justify-center">
                <Link to="/customer/request">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Wrench className="w-4 h-4 mr-2" />
                    Request Service
                  </Button>
                </Link>
                <Link to="/marketplace">
                  <Button variant="outline">
                    <Package className="w-4 h-4 mr-2" />
                    Browse Marketplace
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="cart-page">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Continue Shopping
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-8 font-['Chivo']">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Services */}
            {services.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-blue-600" />
                    Services ({services.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {services.map((item) => (
                    <div key={`${item.type}-${item.id}`} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Wrench className="w-8 h-8 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        <p className="text-sm text-gray-500">{item.description}</p>
                        <p className="text-sm text-gray-400">+ 7% tax</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-blue-600">${item.price.toFixed(2)}</p>
                        <button 
                          onClick={() => removeFromCart(item.id, item.type)}
                          className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1 mt-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between pt-4 border-t">
                    <span className="font-medium">Services Subtotal (incl. 7% tax)</span>
                    <span className="font-bold text-blue-600">${getServiceTotal().toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Products */}
            {products.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-green-600" />
                    Marketplace Items ({products.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {products.map((item) => (
                    <div key={`${item.type}-${item.id}`} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                        {item.image ? (
                          <img src={item.image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Package className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        <p className="text-sm text-gray-500">Seller: {item.seller_name}</p>
                        <p className="text-sm text-gray-400">+ 1% platform fee</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => updateQuantity(item.id, item.type, (item.quantity || 1) - 1)}
                          className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity || 1}</span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.type, (item.quantity || 1) + 1)}
                          className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">${(item.price * (item.quantity || 1)).toFixed(2)}</p>
                        <button 
                          onClick={() => removeFromCart(item.id, item.type)}
                          className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1 mt-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Shipping Options */}
                  <div className="pt-4 border-t">
                    <Label className="mb-2 block">Shipping Option</Label>
                    <Select value={shipping} onValueChange={setShipping}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {shippingOptions.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>
                            <div className="flex justify-between items-center w-full">
                              <span>{opt.name}</span>
                              <span className="text-gray-500 ml-4">
                                {opt.price === 0 ? 'Free' : `$${opt.price.toFixed(2)}`} - {opt.days}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex justify-between pt-4 border-t">
                    <span className="font-medium">Products Subtotal (incl. 1% fee)</span>
                    <span className="font-bold text-green-600">${getProductTotal().toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Checkout Type Selection */}
                {services.length > 0 && products.length > 0 && (
                  <div>
                    <Label className="mb-2 block">Checkout</Label>
                    <Select value={checkoutType} onValueChange={setCheckoutType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Items</SelectItem>
                        <SelectItem value="services">Services Only</SelectItem>
                        <SelectItem value="products">Products Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2 text-sm">
                  {(checkoutType === 'all' || checkoutType === 'services') && services.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Services (incl. tax)</span>
                      <span>${getServiceTotal().toFixed(2)}</span>
                    </div>
                  )}
                  {(checkoutType === 'all' || checkoutType === 'products') && products.length > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Products (incl. fee)</span>
                        <span>${getProductTotal().toFixed(2)}</span>
                      </div>
                      {shipping !== 'pickup' && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Shipping</span>
                          <span>${selectedShipping?.price.toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-blue-600">${calculateTotal().toFixed(2)}</span>
                  </div>
                </div>

                <Button 
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                  onClick={handleCheckout}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Checkout ${calculateTotal().toFixed(2)}
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Secure checkout powered by Stripe
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Stripe Payment Modal */}
      <StripeCheckoutModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        amount={calculateTotal()}
        orderId={currentOrderId}
        itemName={`Cart (${cartItems.length} items)`}
        onSuccess={handlePaymentSuccess}
        paymentType="marketplace"
      />
    </div>
  );
}
